import _ from 'lodash';
import axios from 'axios';
import { compile, parse } from 'path-to-regexp';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

import { blockUser, changeExpireValue, resetUser, resetStore } from '../redux/actions/loginAction';
import store from '../redux/store';

import {
  getAccessToken,
  getAuthDataFromLocalStorage,
  setAuthDataToLocalStorage,
  removeAuthDataFromLocalStorage,
} from './authorization';
import { refresh } from '../api/app';

const urlsWithoutBlock = ['/auth/signIn'];

axios.defaults.baseURL = process.env.REACT_APP_URL_API;
axios.interceptors.request.use(
  async config => {
    const token = getAccessToken();

    if (config.headers.Auth !== 'delete' && token) {
      config.headers.Authorization = config.headers.Auth ? config.headers.Auth : token;
    }
    delete config.headers.Auth;

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

createAuthRefreshInterceptor(
  axios,
  failedRequest => {
    const tokenPrev = failedRequest.response.config.headers['Authorization'];

    return navigator.locks.request('auth_refresh_lock', async () => {
      const tokenNow = getAccessToken();

      if (tokenPrev !== tokenNow) {
        failedRequest.response.config.headers['Authorization'] = tokenNow;
        return Promise.resolve();
      }

      try {
        const authData = getAuthDataFromLocalStorage();
        const results = await refresh(authData.refreshToken);
        setAuthDataToLocalStorage(results);
        document.dispatchEvent(new CustomEvent('refreshToken'));
        failedRequest.response.config.headers['Authorization'] = results.token;

        return Promise.resolve();
      } catch (error) {
        if (error.status === 401) {
          removeAuthDataFromLocalStorage();
          store.dispatch(resetStore());
        }
        return Promise.reject(error);
      }
    });
  },
  {
    statusCodes: [401],
  }
);

axios.interceptors.response.use(
  response => response,
  function (error) {
    const { response } = error;
    if (response && response.status === 429 && !urlsWithoutBlock.includes(response.config.url)) {
      store.dispatch(blockUser());
    }
    return Promise.reject(error);
  }
);

const fetch = options => {
  let {
    method = 'get',
    data,
    url,
    headers,
    responseType,
    withCredentials = false,
    skipAuthRefresh = false,
    signal,
    timeout,
    onProgress = () => {},
  } = options;
  
  headers = headers || {};
  const cloneData = data instanceof FormData ? data : _.cloneDeep(data);

  try {
    let domin = '';
    
    if (
      url.match(/[a-zA-z]+:\/\/[^/]*/) &&
      url.match(/[a-zA-z]+:\/\/[^/]*/) === process.env.REACT_APP_URL_API
    ) {
      [domin] = url.match(/[a-zA-z]+:\/\/[^/]*/);
      url = url.slice(domin.length);
    }
    
    const match = parse(url);
    url = compile(url)(data);
    
    for (let item of match) {
      if (item instanceof Object && item.name in cloneData) {
        delete cloneData[item.name];
      }
    }
    
    url = domin + url;
  } catch (e) {
    console.log(e.message);
  }
  
  switch (method.toLowerCase()) {
    case 'get':
      return axios.get(url, {
        params: cloneData,
        headers: headers,
        responseType: responseType,
        signal,
        withCredentials,
        onDownloadProgress: onProgress,
      });
    case 'delete':
      return axios.delete(url, {
        data: cloneData,
      });
    case 'post':
      if (withCredentials) {
        return axios.post(url, cloneData, { headers, withCredentials, signal, timeout, skipAuthRefresh });
      } else {
        return axios.post(url, cloneData, { headers, skipAuthRefresh });
      }
    case 'put':
      return axios.put(url, cloneData);
    case 'patch':
      return axios.patch(url, cloneData);
    default:
      return axios(options);
  }
};

export default function request(options) {
  return fetch(options)
    .then(response => {
      const { statusText, status, data, headers } = response;

      const resolveResponse = {
        success: true,
        message: statusText,
        code: status,
        results: data,
      };

      if (options.returnHeaders) resolveResponse.headers = headers;

      return Promise.resolve(resolveResponse);
    })
    .catch(error => {
      const { response } = error;
      let errorText;
      let code;
      let errorCode;
      let message;
      
      if (response && response instanceof Object) {
        const { statusText, status } = response;
        let { data } = response;

        if (data instanceof ArrayBuffer) {
          try {
            const decoder = new TextDecoder('utf-8');
            const text = decoder.decode(data);
            data = JSON.parse(text);
          } catch (e) {
            console.error('Failed to parse ArrayBuffer error:', e);
          }
        }

        if (typeof data.error === 'string') errorText = data.error;

        if (typeof data.body === 'string') {
          try {
            data = JSON.parse(data.body);
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }
        }
  
        if (typeof data === 'object' && data.errorCode) {
          errorCode = data.errorCode;
        }

        code = status;
        message = data.message || errorText || statusText;
      } else {
        code = 600;
        message = error.message || 'Network error';
      }

      if (code === 400 && message === 'Password is expired') {
        store.dispatch(changeExpireValue(0));
      }

      if (code === 403 && message === 'User is blocked') {
        store.dispatch(resetUser());
      }

      return Promise.reject({ success: false, code, message: message, errorCode });
    });
}
